<template>
  <div class="batch-order-create-page">
    <div class="page-body">
      <div class="page-header">
        <h2 class="page-title">批量生成定制订单 - 配置生成规则</h2>
        <p class="page-desc">
          选择企业和部门，配置不同余额区间对应的定制方案，并选择员工余额的统计时间、订单支付截止日期及出游日期，点击下一步后，系统将根据规则自动生成定制订单（余额为0的员工将自动跳过）。
        </p>
      </div>

      <div class="top-sections">
        <!-- 1. 企业与组织范围 -->
        <div class="section-card scope-card">
          <div class="section-title">
            <span class="section-index">1</span>
            <span>企业与组织范围</span>
          </div>
          <div class="field-row">
            <span class="field-label">企业名称</span>
            <a-select
              v-model="form.enterpriseId"
              class="enterprise-select"
              placeholder="请选择企业"
              show-search
              option-filter-prop="children"
              :loading="enterpriseLoading"
              @change="onEnterpriseChange"
            >
              <a-select-option
                v-for="item in enterpriseOptions"
                :key="item.value"
                :value="item.value"
              >
                {{ item.label }}
              </a-select-option>
            </a-select>
          </div>
          <div class="field-row org-field">
            <span class="field-label">组织架构</span>
            <div class="org-tree-box">
              <div class="org-tree-header">
                <span class="selected-count">已选 {{ checkedCount }}/{{ totalDeptCount }}</span>
              </div>
              <div class="org-tree-body">
                <a-spin :spinning="deptLoading">
                  <a-empty
                    v-if="!deptLoading && !deptTreeData.length"
                    description="请先选择企业"
                  />
                  <a-tree
                    v-else-if="deptTreeData.length"
                    v-model="form.checkedDeptKeys"
                    checkable
                    :tree-data="deptTreeData"
                    :replace-fields="deptReplaceFields"
                    :expanded-keys="expandedKeys"
                    :auto-expand-parent="true"
                    @expand="onExpand"
                    @check="onDeptCheck"
                  />
                </a-spin>
              </div>
            </div>
          </div>
        </div>

        <!-- 2. 余额统计时间 -->
        <div class="section-card time-card">
          <div class="section-title">
            <span class="section-index">2</span>
            <span>余额统计时间</span>
          </div>
          <div class="field-row time-field">
            <span class="field-label">计算时间:</span>
            <a-radio-group v-model="form.balanceTimeType">
              <a-radio :value="1">当前实时余额</a-radio>
            </a-radio-group>
          </div>
        </div>
      </div>

      <!-- 3. 配置余额区间 -->
      <div class="section-card range-card">
        <div class="section-title-row">
          <div class="section-title">
            <span class="section-index">3</span>
            <span>配置余额区间对应定制方案</span>
          </div>
          <a-button icon="plus" @click="addRange">添加额度区间</a-button>
        </div>
        <p class="section-tip">
          系统将根据员工余额自动匹配对应的定制方案，不同余额区间可选择不同的定制方案。
        </p>

        <a-table
          class="range-table"
          :columns="rangeColumns"
          :data-source="form.ranges"
          :pagination="false"
          row-key="id"
          bordered
          size="middle"
        >
          <template slot="balanceRange" slot-scope="text, record">
            <span v-if="record.isSkip" class="placeholder-dash">--</span>
            <div v-else class="range-inputs">
              <a-input-number
                v-model="record.minAmount"
                :min="0"
                :precision="0"
                class="range-input"
              />
              <span class="range-sep">—</span>
              <a-input-number
                v-model="record.maxAmount"
                :min="0"
                :precision="0"
                class="range-input"
              />
            </div>
          </template>

          <template slot="plan" slot-scope="text, record">
            <span v-if="record.isSkip" class="skip-plan">自动跳过 (不生成订单)</span>
            <a-input
              v-else
              v-model="record.planName"
              class="plan-input"
              placeholder="请输入定制方案"
              allowClear
            />
          </template>

          <template slot="pdf" slot-scope="text, record">
            <span v-if="record.isSkip" class="placeholder-dash">--</span>
            <div v-else class="pdf-cell">
              <a-upload
                name="file"
                :action="uploadFileUrl"
                :file-list="record.fileList"
                :before-upload="beforeUploadPdf"
                :remove="() => handleRemovePdf(record)"
                accept=".pdf"
                @change="info => handlePdfChange(info, record)"
              >
                <a v-if="!record.fileList.length" class="upload-link">
                  <a-icon type="upload" /> 上传PDF
                </a>
              </a-upload>
            </div>
          </template>

          <template slot="payDeadline" slot-scope="text, record">
            <span v-if="record.isSkip" class="placeholder-dash">--</span>
            <a-date-picker
              v-else
              v-model="record.payDeadline"
              format="YYYY-MM-DD"
              placeholder="选择日期"
              style="width: 100%"
            />
          </template>

          <template slot="travelDate" slot-scope="text, record">
            <span v-if="record.isSkip" class="placeholder-dash">--</span>
            <a-date-picker
              v-else
              v-model="record.travelDate"
              format="YYYY-MM-DD"
              placeholder="选择日期"
              style="width: 100%"
            />
          </template>

          <template slot="action" slot-scope="text, record">
            <span v-if="record.isSkip" class="placeholder-dash">--</span>
            <a v-else class="delete-link" @click="removeRange(record)">删除</a>
          </template>
        </a-table>
      </div>

      <div class="page-footer">
        <div class="info-box">
          <a-icon type="info-circle" class="info-icon" />
          <div class="info-content">
            <p>1. 余额为 0 的员工将自动跳过，不生成订单；</p>
            <p>2. 请确保各余额区间连续且不重叠，系统将按区间匹配对应方案；</p>
            <p>3. 点击「下一步」后，系统将按规则预生成订单，确认后再提交。</p>
          </div>
        </div>
        <div class="footer-actions">
          <a-button class="back-btn" @click="handleBack">返回</a-button>
          <a-button type="primary" class="next-btn" :loading="submitLoading" @click="handleNext">
            下一步：生成订单
          </a-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

let rangeIdSeed = 1

function createRange (overrides = {}) {
  return {
    id: rangeIdSeed++,
    isSkip: false,
    minAmount: undefined,
    maxAmount: undefined,
    planName: '',
    fileList: [],
    pdfUrl: '',
    pdfName: '',
    payDeadline: undefined,
    travelDate: undefined,
    ...overrides
  }
}

export default {
  name: 'BatchorderCreate',
  data () {
    return {
      uploadFileUrl: '',
      enterpriseLoading: false,
      deptLoading: false,
      submitLoading: false,
      form: {
        enterpriseId: undefined,
        checkedDeptKeys: [],
        balanceTimeType: 1, // 当前实时余额
        ranges: [
          createRange({
            isSkip: true
          }),
          createRange()
        ]
      },
      expandedKeys: [],
      enterpriseOptions: [],
      deptTreeData: [],
      deptReplaceFields: {
        title: 'deptName',
        key: 'deptId',
        children: 'children'
      },
      rangeColumns: [
        {
          title: '余额区间 (元)',
          dataIndex: 'balanceRange',
          scopedSlots: { customRender: 'balanceRange' },
          width: 220
        },
        {
          title: '对应定制方案',
          dataIndex: 'plan',
          scopedSlots: { customRender: 'plan' },
          width: 200
        },
        {
          title: '方案PDF',
          dataIndex: 'pdf',
          scopedSlots: { customRender: 'pdf' },
          width: 140
        },
        {
          title: '订单支付截止日期',
          dataIndex: 'payDeadline',
          scopedSlots: { customRender: 'payDeadline' },
          width: 180
        },
        {
          title: '出游日期',
          dataIndex: 'travelDate',
          scopedSlots: { customRender: 'travelDate' },
          width: 180
        },
        {
          title: '操作',
          dataIndex: 'action',
          scopedSlots: { customRender: 'action' },
          width: 80,
          align: 'center'
        }
      ]
    }
  },
  computed: {
    totalDeptCount () {
      return this.countTreeNodes(this.deptTreeData)
    },
    checkedCount () {
      return (this.form.checkedDeptKeys || []).length
    }
  },
  created () {
    this.uploadFileUrl = this.getUploadFileUrl()
    this.loadEnterpriseList()
  },
  methods: {
    getUploadFileUrl () {
      const appConfig = this.getAppConfig()
      return appConfig.baseUrl + (appConfig.isDev ? '/' : '') + 'base/upload'
    },
    loadEnterpriseList () {
      this.enterpriseLoading = true
      this.getHttpServices()
        .getMemberAuthService()
        .post('member/v3/auth/getEnterpriseList', {
          limitDto: {
            pageNum: 1,
            pageSize: 1000
          }
        })
        .then(res => {
          const list = (res && res.data) || []
          this.enterpriseOptions = list
            .filter(item => item.status === 1)
            .map(item => ({
              value: item.enterpriseId,
              label: item.enterpriseFullName
            }))
          if (!this.form.enterpriseId && this.enterpriseOptions.length) {
            this.form.enterpriseId = this.enterpriseOptions[0].value
            this.loadDeptTree(this.form.enterpriseId)
          }
        })
        .catch(() => {
          this.$message.error('获取企业列表失败，请重试')
        })
        .finally(() => {
          this.enterpriseLoading = false
        })
    },
    getEnterpriseRootKey (enterpriseId) {
      return `enterprise-root-${enterpriseId}`
    },
    getEnterpriseName (enterpriseId) {
      const found = this.enterpriseOptions.find(item => item.value === enterpriseId)
      return found ? found.label : '全部'
    },
    // 接口只返回部门树，补一层企业根节点用于全选
    wrapDeptTreeWithEnterpriseRoot (enterpriseId, deptList) {
      return [{
        deptId: this.getEnterpriseRootKey(enterpriseId),
        deptName: this.getEnterpriseName(enterpriseId),
        children: deptList || []
      }]
    },
    loadDeptTree (enterpriseId) {
      if (!enterpriseId) {
        this.deptTreeData = []
        this.expandedKeys = []
        this.form.checkedDeptKeys = []
        return
      }
      this.deptLoading = true
      this.getHttpServices()
        .getMemberAuthService()
        .post(`member/v3/auth/getDeptTree/${enterpriseId}`)
        .then(data => {
          this.deptTreeData = this.wrapDeptTreeWithEnterpriseRoot(enterpriseId, data || [])
          this.expandedKeys = this.collectExpandKeys(this.deptTreeData)
          this.form.checkedDeptKeys = []
        })
        .catch(() => {
          this.deptTreeData = []
          this.expandedKeys = []
          this.form.checkedDeptKeys = []
          this.$message.error('获取组织架构失败，请重试')
        })
        .finally(() => {
          this.deptLoading = false
        })
    },
    onEnterpriseChange (enterpriseId) {
      this.loadDeptTree(enterpriseId)
    },
    // 提交时取真实部门 id（排除企业根节点）
    getSelectedDeptIds () {
      const rootKey = this.getEnterpriseRootKey(this.form.enterpriseId)
      return (this.form.checkedDeptKeys || [])
        .filter(key => key !== rootKey)
        .map(key => Number(key))
        .filter(id => !Number.isNaN(id))
    },
    formatDate (val) {
      if (!val) return ''
      return moment(val).format('YYYY-MM-DD')
    },
    // 提交时补当天最后一秒
    formatDateEndOfDay (val) {
      if (!val) return ''
      return moment(val).format('YYYY-MM-DD') + ' 23:59:59'
    },
    getEditableRanges () {
      return (this.form.ranges || []).filter(item => !item.isSkip)
    },
    buildCreateParams () {
      const priceItemS = this.getEditableRanges().map((item, index) => ({
        endPrice: Number(item.maxAmount),
        name: (item.planName || '').trim(),
        orderEndTime: this.formatDateEndOfDay(item.payDeadline),
        pdfName: item.pdfName,
        pdfUrl: item.pdfUrl,
        sort: index + 1,
        startPrice: Number(item.minAmount),
        travelDate: this.formatDate(item.travelDate)
      }))
      return {
        deptIds: this.getSelectedDeptIds(),
        enterpriseId: this.form.enterpriseId,
        priceItemS
      }
    },
    validateForm () {
      if (!this.form.enterpriseId) {
        this.$message.error('请选择企业')
        return false
      }
      if (!this.getSelectedDeptIds().length) {
        this.$message.error('请选择组织架构')
        return false
      }
      const ranges = this.getEditableRanges()
      if (!ranges.length) {
        this.$message.error('请至少添加一个余额区间')
        return false
      }
      for (let i = 0; i < ranges.length; i++) {
        const row = ranges[i]
        const rowNo = i + 1
        if (row.minAmount === undefined || row.minAmount === null || row.minAmount === '') {
          this.$message.error(`请填写第 ${rowNo} 行余额区间起始值`)
          return false
        }
        if (row.maxAmount === undefined || row.maxAmount === null || row.maxAmount === '') {
          this.$message.error(`请填写第 ${rowNo} 行余额区间结束值`)
          return false
        }
        if (Number(row.minAmount) > Number(row.maxAmount)) {
          this.$message.error(`第 ${rowNo} 行余额区间起始值不能大于结束值`)
          return false
        }
        if (!(row.planName || '').trim()) {
          this.$message.error(`请填写第 ${rowNo} 行定制方案`)
          return false
        }
        if (!row.pdfUrl) {
          this.$message.error(`请上传第 ${rowNo} 行方案 PDF`)
          return false
        }
        if (!row.payDeadline) {
          this.$message.error(`请选择第 ${rowNo} 行订单支付截止日期`)
          return false
        }
        if (!row.travelDate) {
          this.$message.error(`请选择第 ${rowNo} 行出游日期`)
          return false
        }
      }
      return true
    },
    collectExpandKeys (nodes, keys = []) {
      if (!nodes || !nodes.length) return keys
      nodes.forEach(node => {
        if ((node.children && node.children.length) || String(node.deptId).startsWith('enterprise-root-')) {
          keys.push(node.deptId)
          this.collectExpandKeys(node.children, keys)
        }
      })
      return keys
    },
    countTreeNodes (nodes) {
      if (!nodes || !nodes.length) return 0
      return nodes.reduce((sum, node) => {
        return sum + 1 + this.countTreeNodes(node.children)
      }, 0)
    },
    onExpand (expandedKeys) {
      this.expandedKeys = expandedKeys
    },
    onDeptCheck (checkedKeys) {
      this.form.checkedDeptKeys = checkedKeys
    },
    addRange () {
      const lastEditable = [...this.form.ranges].reverse().find(item => !item.isSkip)
      const nextMin = lastEditable && lastEditable.maxAmount != null
        ? Number(lastEditable.maxAmount) + 1
        : undefined
      this.form.ranges.push(createRange({
        minAmount: nextMin,
        maxAmount: undefined,
        planName: ''
      }))
    },
    removeRange (record) {
      if (record.isSkip) return
      this.form.ranges = this.form.ranges.filter(item => item.id !== record.id)
    },
    beforeUploadPdf (file) {
      const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name)
      if (!isPdf) {
        this.$message.error('仅支持上传 PDF 文件')
        return false
      }
      return true
    },
    handlePdfChange ({ file, fileList }, record) {
      // 每行只保留一个 PDF
      let list = fileList.slice(-1)
      list = list.map(item => {
        if (item.response) {
          // 接口返回 { status: 1, data: url }
          item.url = item.response.data
        }
        return item
      })
      record.fileList = list
      if (file.status === 'done') {
        if (file.response && file.response.status === 1 && file.response.data) {
          record.pdfUrl = file.response.data
          record.pdfName = file.name
          this.$message.success('上传成功')
        } else {
          record.fileList = []
          record.pdfUrl = ''
          record.pdfName = ''
          this.$message.error((file.response && file.response.msg) || '上传失败，请重试')
        }
      } else if (file.status === 'error') {
        record.fileList = []
        record.pdfUrl = ''
        record.pdfName = ''
        this.$message.error('上传失败，请重试')
      }
    },
    handleRemovePdf (record) {
      record.fileList = []
      record.pdfUrl = ''
      record.pdfName = ''
      return true
    },
    handleBack () {
      this.$router.push({
        name: 'TravelMode',
        query: {
          tab: 'batchorder',
          serverUniqueId: this.$route.query.serverUniqueId
        }
      })
    },
    handleNext () {
      if (!this.validateForm()) return
      const params = this.buildCreateParams()
      this.submitLoading = true
      this.getHttpServices()
        .getNewServeService()
        .post('/enterpriseTravelSetting/create', params)
        .then(() => {
          this.$message.success('创建成功')
          this.handleBack()
        })
        .catch(err => {
          this.$message.error((err && err.msg) || '创建失败，请重试')
        })
        .finally(() => {
          this.submitLoading = false
        })
    }
  }
}
</script>

<style scoped lang="less">
@primary: #faad14;
@text-main: rgba(0, 0, 0, 0.85);
@text-secondary: rgba(0, 0, 0, 0.45);
@border: #e8e8e8;

.batch-order-create-page {
  min-height: 100%;
  background: #f5f5f5;
}

.page-body {
  margin: 16px;
  padding: 24px;
  background: #fff;
  border-radius: 4px;
}

.page-header {
  margin-bottom: 24px;
}

.page-title {
  margin: 0 0 8px;
  font-size: 20px;
  font-weight: 600;
  color: @text-main;
  line-height: 28px;
}

.page-desc {
  margin: 0;
  font-size: 13px;
  line-height: 22px;
  color: @text-secondary;
}

.top-sections {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.section-card {
  border: 1px solid @border;
  border-radius: 4px;
  padding: 20px;
  background: #fff;
}

.scope-card {
  flex: 1.4;
  min-width: 0;
}

.time-card {
  flex: 1;
  min-width: 280px;
}

.range-card {
  margin-bottom: 24px;
}

.section-title {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  font-size: 16px;
  font-weight: 600;
  color: @text-main;
}

.section-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;

  .section-title {
    margin-bottom: 0;
  }
}

.section-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin-right: 8px;
  border-radius: 50%;
  background: @primary;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
}

.section-tip {
  margin: 0 0 16px;
  font-size: 13px;
  color: @text-secondary;
  line-height: 22px;
}

.field-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
}

.field-label {
  flex: none;
  width: 72px;
  margin-right: 12px;
  line-height: 32px;
  color: rgba(0, 0, 0, 0.65);
}

.enterprise-select {
  width: 100%;
  max-width: 420px;
}

.org-field {
  align-items: stretch;
}

.org-tree-box {
  flex: 1;
  min-width: 0;
  border: 1px solid @border;
  border-radius: 4px;
  overflow: hidden;
}

.org-tree-header {
  display: flex;
  justify-content: flex-end;
  padding: 8px 12px;
  border-bottom: 1px solid @border;
  background: #fafafa;
}

.selected-count {
  color: #1890ff;
  font-size: 13px;
}

.org-tree-body {
  max-height: 280px;
  overflow: auto;
  padding: 8px 4px;
}

.time-field {
  align-items: center;

  /deep/ .ant-radio-group {
    line-height: 32px;
  }
}

.range-table {
  /deep/ .ant-table-thead > tr > th {
    background: #fafafa;
    font-weight: 500;
  }
}

.range-inputs {
  display: flex;
  align-items: center;
}

.range-input {
  width: 88px;
}

.range-sep {
  margin: 0 8px;
  color: @text-secondary;
}

.skip-text,
.skip-plan,
.placeholder-dash {
  color: @text-secondary;
}

.plan-input {
  width: 100%;
}

.pdf-cell {
  /deep/ .ant-upload-list {
    margin-top: 4px;
  }
}

.upload-link {
  color: #1890ff;
}

.delete-link {
  color: #1890ff;
}

.page-footer {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
}

.info-box {
  display: flex;
  flex: 1;
  min-width: 0;
  padding: 12px 16px;
  background: #e6f7ff;
  border: 1px solid #91d5ff;
  border-radius: 4px;
}

.info-icon {
  margin-right: 8px;
  margin-top: 3px;
  color: #1890ff;
  font-size: 16px;
}

.info-content {
  flex: 1;

  p {
    margin: 0;
    font-size: 13px;
    line-height: 22px;
    color: rgba(0, 0, 0, 0.65);
  }
}

.footer-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: none;
}

.back-btn,
.next-btn {
  min-width: 120px;
  height: 40px;
  font-size: 15px;
}

.next-btn {
  min-width: 168px;
}

@media (max-width: 1200px) {
  .top-sections {
    flex-direction: column;
  }
}
</style>
